import React, { useState, useRef, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import api from '../../../api';

const BulkOperations = ({
  showBulkAddModal,
  onCloseBulkAdd,
  onBulkAddComplete,
  tenantId,
  isViewOnly
}) => {
  const [status, setStatus] = useState('idle'); 
  const [progress, setProgress] = useState({
    current: 0,
    total: 0,
    step: 0,
    stepName: '',
    subStepName: '',
    startTime: null,
    elapsedTime: '0s',
    percentage: 0
  });
  const [report, setReport] = useState(null);
  const [errors, setErrors] = useState([]);
  const [errorSummary, setErrorSummary] = useState({});
  const [academicStructure, setAcademicStructure] = useState(null);
  const [academicType, setAcademicType] = useState({
    hasSemester: false,
    hasTerms: false
  });
  const [fileInfo, setFileInfo] = useState({
    name: '',
    size: 0,
    records: 0
  });

  const fileInputRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const startTimeRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Fetch academic structure
  const fetchAcademicStructure = useCallback(async () => {
    try {
      const response = await api.get(`/api/students/academic-structure?tenantId=${tenantId}`);
      const structure = response.data.structure || [];
      setAcademicStructure(structure);
      // Determine academic types
      let hasSemester = false;
      let hasTerms = false;
      structure.forEach(pt => {
        pt.programs.forEach(prg => {
          prg.subjectMappings.forEach(mapping => {
            if (mapping.academicYear.isSemesterBased) {
              hasSemester = true;
            } else {
              hasTerms = true;
            }
          });
        });
      });
      setAcademicType({ hasSemester, hasTerms });
    } catch (error) {
      console.error('Error fetching academic structure:', error);
    }
  }, [tenantId]);

  useEffect(() => {
    if (showBulkAddModal) {
      resetState();
      fetchAcademicStructure();
    }
  }, [showBulkAddModal, fetchAcademicStructure]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const resetState = () => {
    setStatus('idle');
    setReport(null);
    setErrors([]);
    setErrorSummary({});
    setProgress({
      current: 0,
      total: 0,
      step: 0,
      stepName: '',
      subStepName: '',
      startTime: null,
      elapsedTime: '0s',
      percentage: 0
    });
    setAcademicStructure(null);
    setAcademicType({ hasSemester: false, hasTerms: false });
    setFileInfo({ name: '', size: 0, records: 0 });
    if (fileInputRef.current) fileInputRef.current.value = '';
    
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    startTimeRef.current = null;
    abortControllerRef.current = new AbortController();
  };

  // Update elapsed time
  useEffect(() => {
    let intervalId = null;

    if ((status === 'uploading' || status === 'parsing' || status === 'validating' || status === 'processing') && progress.startTime) {
      startTimeRef.current = progress.startTime;
      intervalId = setInterval(() => {
        setProgress(prev => {
          if (!prev.startTime) return prev;
          const elapsed = Math.floor((Date.now() - prev.startTime) / 1000);
          const minutes = Math.floor(elapsed / 60);
          const seconds = elapsed % 60;
          return {
            ...prev,
            elapsedTime: minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`
          };
        });
      }, 1000);
      progressIntervalRef.current = intervalId;
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [status, progress.startTime]);

  // --- 1. TEMPLATE GENERATOR (FIXED) ---
  const handleDownloadTemplate = async () => {
    try {
      setStatus('generating');
      setProgress({
        current: 0,
        total: 0,
        step: 1,
        stepName: 'Generating Template',
        subStepName: 'Building academic reference data...',
        startTime: Date.now(),
        elapsedTime: '0s',
        percentage: 10
      });

      // Use cached structure or fetch if not available
      let structure = academicStructure;
      if (!structure) {
        const response = await api.get(`/api/students/academic-structure?tenantId=${tenantId}`);
        structure = response.data.structure || [];
      }

      setProgress(prev => ({ ...prev, subStepName: 'Processing academic structure...', percentage: 30 }));

      // Build reference data with ALL combinations
      const referenceRows = [];
      const seenCombinations = new Set();

      // Process each program type
      structure.forEach(pt => {
        const programTypeName = pt.programType.name;
        
        pt.programs.forEach(prg => {
          const programName = prg.program.name;
          
          // Collect all academic years from mappings
          const academicYearsMap = new Map();
          
          // First, collect all unique academic years from mappings
          prg.subjectMappings.forEach(mapping => {
            const ay = mapping.academicYear;
            if (!academicYearsMap.has(ay._id)) {
              academicYearsMap.set(ay._id, ay);
            }
          });
          
          // If no academic years found, add placeholder
          if (academicYearsMap.size === 0) {
            const key = `${programTypeName}|${programName}|`;
            if (!seenCombinations.has(key)) {
              seenCombinations.add(key);
              const row = {
                "Program Type": programTypeName,
                "Program Name": programName,
                "Academic Year": "",
              };
              if (academicType.hasSemester) row["Semester"] = "";
              if (academicType.hasTerms) row["Term"] = "";
              row["Available Class"] = "";
              referenceRows.push(row);
            }
            return;
          }
          
          // Process each academic year
          academicYearsMap.forEach(academicYear => {
            const ayName = academicYear.name;
            const isSemesterBased = academicYear.isSemesterBased;
            
            if (isSemesterBased && Array.isArray(academicYear.semesters) && academicYear.semesters.length > 0) {
              // Process semesters
              academicYear.semesters.forEach(sem => {
                const semesterName = sem.semesterName || sem.semesterLabel || `Semester ${sem.semesterNumber}`;
                
                // Find mapping for this semester
                const mapping = prg.subjectMappings.find(m => 
                  m.academicYear._id === academicYear._id &&
                  m.semester && 
                  m.semester._id === sem._id
                );
                
                const programKey = `${programTypeName}|${programName}|${ayName}|${semesterName}`;
                if (!seenCombinations.has(programKey)) {
                  seenCombinations.add(programKey);
                  
                  // Get classes for this semester
                  const classesForThisSemester = mapping && mapping.classes ? 
                    mapping.classes.filter(cls => 
                      cls.isSemesterBased && 
                      cls.semesterNumber === sem.semesterNumber
                    ) : [];
                  
                  if (classesForThisSemester.length > 0) {
                    classesForThisSemester.forEach(cls => {
                      const classKey = `${programTypeName}|${programName}|${ayName}|${semesterName}|${cls.className}`;
                      if (!seenCombinations.has(classKey)) {
                        seenCombinations.add(classKey);
                        referenceRows.push({
                          "Program Type": programTypeName,
                          "Program Name": programName,
                          "Academic Year": ayName,
                          "Semester": semesterName,
                          "Available Class": cls.className
                        });
                      }
                    });
                  } else {
                    // Add row with empty class even if no mapping exists
                    referenceRows.push({
                      "Program Type": programTypeName,
                      "Program Name": programName,
                      "Academic Year": ayName,
                      "Semester": semesterName,
                      "Available Class": ""
                    });
                  }
                }
              });
            } else if (Array.isArray(academicYear.terms) && academicYear.terms.length > 0) {
              // Process terms - THIS IS THE FIXED PART
              academicYear.terms.forEach(term => {
                const termName = term.termLabel || term.name || `Term ${term.termNumber}`;
                
                // Find mapping for this term
                const mapping = prg.subjectMappings.find(m => 
                  m.academicYear._id === academicYear._id &&
                  m.term && 
                  m.term._id === term._id
                );
                
                const programKey = `${programTypeName}|${programName}|${ayName}|${termName}`;
                if (!seenCombinations.has(programKey)) {
                  seenCombinations.add(programKey);
                  
                  // Get classes for this term
                  const classesForThisTerm = mapping && mapping.classes ? 
                    mapping.classes.filter(cls => 
                      !cls.isSemesterBased && 
                      cls.termNumber === term.termNumber
                    ) : [];
                  
                  if (classesForThisTerm.length > 0) {
                    classesForThisTerm.forEach(cls => {
                      const classKey = `${programTypeName}|${programName}|${ayName}|${termName}|${cls.className}`;
                      if (!seenCombinations.has(classKey)) {
                        seenCombinations.add(classKey);
                        referenceRows.push({
                          "Program Type": programTypeName,
                          "Program Name": programName,
                          "Academic Year": ayName,
                          "Term": termName,
                          "Available Class": cls.className
                        });
                      }
                    });
                  } else {
                    // CRITICAL FIX: ALWAYS add row even if no classes exist for this term
                    // This ensures ALL standards appear in the reference sheet
                    referenceRows.push({
                      "Program Type": programTypeName,
                      "Program Name": programName,
                      "Academic Year": ayName,
                      "Term": termName,
                      "Available Class": ""
                    });
                  }
                }
              });
            } else {
              // Academic year has no semesters or terms defined
              const key = `${programTypeName}|${programName}|${ayName}`;
              if (!seenCombinations.has(key)) {
                seenCombinations.add(key);
                const row = {
                  "Program Type": programTypeName,
                  "Program Name": programName,
                  "Academic Year": ayName,
                };
                if (academicType.hasSemester) row["Semester"] = "";
                if (academicType.hasTerms) row["Term"] = "";
                row["Available Class"] = "";
                referenceRows.push(row);
              }
            }
          });
        });
      });

      setProgress(prev => ({ 
        ...prev, 
        subStepName: `Creating Excel workbook with ${referenceRows.length} reference combinations...`,
        percentage: 70 
      }));

      // Sort reference rows
      referenceRows.sort((a, b) => {
        if (a["Program Type"] !== b["Program Type"]) return a["Program Type"].localeCompare(b["Program Type"]);
        if (a["Program Name"] !== b["Program Name"]) return a["Program Name"].localeCompare(b["Program Name"]);
        if (a["Academic Year"] !== b["Academic Year"]) return a["Academic Year"].localeCompare(b["Academic Year"]);
        const aSemester = a["Semester"] || a["Term"] || "";
        const bSemester = b["Semester"] || b["Term"] || "";
        return aSemester.localeCompare(bSemester);
      });

      // Build headers dynamically
      let studentHeaders = [
        "Program Type*",
        "Program Name*",
        "Academic Year*"
      ];
      
      if (academicType.hasSemester) {
        studentHeaders.push("Semester*");
      }
      if (academicType.hasTerms) {
        studentHeaders.push("Term*");
      }
      
      studentHeaders.push(
        "Preferred Class",
        "First Name*",
        "Last Name*",
        "Date of Birth* (DD/MM/YYYY)",
        "Gender* (Male/Female/Other)",
        "Student Country Code* (+91)",
        "Student Phone* (10 digits)",
        "Student Email*",
        "Blood Group",
        "Category",
        "Nationality",
        "Admission Type",
        "Parent Name*",
        "Parent Country Code (+91)",
        "Parent Phone (10 digits)",
        "Parent Email",
        "Permanent Address",
        "City",
        "State",
        "Pincode",
        "Country",
        "USN",
        "Remarks"
      );

      // Sample row
      const sampleRow = [
        "Example: Science",
        "Example: PCMC",
        "Example: 2024-2025",
        ...(academicType.hasSemester ? ["Example: 3rd Semester"] : []),
        ...(academicType.hasTerms ? ["Example: 2 PUC"] : []),
        "Example: Class A",
        "John",
        "Doe",
        "15/05/2000",
        "Male",
        "+91",
        "9876543210",
        "john.doe@example.com",
        "O+",
        "General",
        "Indian",
        "Regular",
        "Robert Doe",
        "+91",
        "9876543211",
        "parent@example.com",
        "123 Main Street",
        "Bangalore",
        "Karnataka",
        "560001",
        "India",
        "",
        "Scholarship student"
      ];

      // Create Student Data sheet
      const wsStudentData = XLSX.utils.aoa_to_sheet([studentHeaders, sampleRow]);
      wsStudentData['!cols'] = studentHeaders.map(h => ({ wch: h.includes('*') ? 25 : 20 }));

      // Create Reference sheet
      const refHeaders = ["Program Type", "Program Name", "Academic Year"];
      if (academicType.hasSemester) refHeaders.push("Semester");
      if (academicType.hasTerms) refHeaders.push("Term");
      refHeaders.push("Available Class");

      const wsReference = XLSX.utils.json_to_sheet(referenceRows, { header: refHeaders });
      wsReference['!cols'] = refHeaders.map(() => ({ wch: 25 }));

      // Create Instructions sheet
      const instructionsData = [
        ["STUDENT BULK IMPORT - INSTRUCTIONS"],
        [],
        ["1. Download this template and fill in student data"],
        ["2. All columns marked with * are REQUIRED"],
        ["3. Check the 'Reference' sheet for valid academic combinations"],
        ["4. Date format must be DD/MM/YYYY (e.g., 15/05/2000)"],
        ["5. Phone numbers must be 10 digits"],
        ["6. Email must be unique across the system"],
        ["7. Default password for all students: Truyon@1234"],
        [],
        ["📌 REFERENCE SHEET GUIDE:"],
        ["• Every valid Program Type + Program Name + Academic Year + Semester/Term is listed"],
        ["• 'Available Class' shows which classes exist for that specific combination"],
        ["• If 'Available Class' is empty, no class exists for that combination yet"],
        ["• You can still add students to that combination - they will be unallocated"],
        ["• Classes can be created later and students can be reassigned"],
        [],
        academicType.hasSemester ? ["⚠️ This institution uses SEMESTER-based academic years"] : [],
        academicType.hasTerms ? ["⚠️ This institution uses TERM-based academic years"] : [],
        [],
        ["📊 STATISTICS:"],
        [`• Total academic combinations: ${referenceRows.length}`],
        [`• Combinations with existing classes: ${referenceRows.filter(r => r["Available Class"]).length}`],
        [`• Combinations without classes: ${referenceRows.filter(r => !r["Available Class"]).length}`],
        [],
        ["After import, check results for any failed records"]
      ].filter(row => row.length > 0);

      const wsInstructions = XLSX.utils.aoa_to_sheet(instructionsData);
      wsInstructions['!cols'] = [{ wch: 100 }];

      // Create workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, wsStudentData, "Student Data");
      XLSX.utils.book_append_sheet(wb, wsReference, "Reference");
      XLSX.utils.book_append_sheet(wb, wsInstructions, "Instructions");

      // Download file
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      const filename = `Student_Import_Template_${timestamp}.xlsx`;
      XLSX.writeFile(wb, filename);

      setStatus('idle');
      setProgress({ 
        current: 0, 
        total: 0, 
        step: 0, 
        stepName: '', 
        subStepName: '', 
        startTime: null, 
        elapsedTime: '0s',
        percentage: 0 
      });

    } catch (error) {
      console.error("Template generation error:", error);
      setStatus('error');
      setErrors([{
        row: 0,
        email: '',
        name: '',
        message: error.message || "Failed to generate template",
        failedField: 'Template Generation'
      }]);
    }
  };

  // --- 2. DATE PARSING ---
  const parseExcelDate = (value) => {
    if (!value && value !== 0) return "";
    
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (/^\d{1,2}[/-]\d{1,2}[/-]\d{2,4}$/.test(trimmed)) {
        return trimmed;
      }
      return trimmed;
    }
    
    if (typeof value === 'number') {
      if (value > 0 && value < 100000) {
        const excelEpoch = new Date(Date.UTC(1899, 11, 30));
        const resultDate = new Date(excelEpoch.getTime() + value * 24 * 60 * 60 * 1000);
        if (resultDate.getFullYear() >= 1900 && resultDate.getFullYear() <= 2100) {
          const day = String(resultDate.getDate()).padStart(2, '0');
          const month = String(resultDate.getMonth() + 1).padStart(2, '0');
          const year = resultDate.getFullYear();
          return `${day}/${month}/${year}`;
        }
      }
    }
    
    if (value instanceof Date && !isNaN(value.getTime())) {
      const day = String(value.getDate()).padStart(2, '0');
      const month = String(value.getMonth() + 1).padStart(2, '0');
      const year = value.getFullYear();
      return `${day}/${month}/${year}`;
    }
    
    return String(value).trim();
  };

  // --- 3. FILE UPLOAD WITH PROGRESS ---
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Cancel any ongoing operation
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    // Show file info
    setFileInfo({
      name: file.name,
      size: (file.size / 1024).toFixed(2) + ' KB',
      records: 0
    });

    setStatus('uploading');
    setErrors([]);
    setErrorSummary({});
    setReport(null);
    setProgress({
      current: 0,
      total: 0,
      step: 1,
      stepName: 'Uploading',
      subStepName: `Reading file: ${file.name} (${(file.size / 1024).toFixed(2)} KB)...`,
      startTime: Date.now(),
      elapsedTime: '0s',
      percentage: 5
    });

    // Use setTimeout to yield
    setTimeout(async () => {
      try {
        // Read file as array buffer
        const data = await file.arrayBuffer();
        
        setProgress(prev => ({
          ...prev,
          step: 2,
          stepName: 'Parsing',
          subStepName: 'Parsing Excel structure...',
          percentage: 20
        }));

        // Parse with optimized options
        setTimeout(() => {
          try {
            const wb = XLSX.read(data, { 
              type: 'array', 
              cellDates: true,
              cellText: false,
              cellNF: false,
              sheetStubs: false
            });
            
            // Find the student data sheet
            const wsname = wb.SheetNames.find(name => 
              name.toLowerCase().includes("student data") || 
              name.toLowerCase().includes("data")
            ) || wb.SheetNames[0];
            
            // Convert to JSON
            const rawData = XLSX.utils.sheet_to_json(wb.Sheets[wsname], { 
              defval: "",
              raw: false,
              blankrows: false
            });
            
            // Update file info with record count
            setFileInfo(prev => ({
              ...prev,
              records: rawData.length
            }));

            // Process data in chunks
            processDataInChunks(rawData);
            
          } catch (parseError) {
            console.error('Parse error:', parseError);
            setStatus('error');
            setErrors([{
              row: 0,
              email: '',
              name: '',
              message: 'Failed to parse Excel file. Please check the format.',
              failedField: 'File Parsing'
            }]);
          }
        }, 0);
        
      } catch (readError) {
        console.error('File read error:', readError);
        setStatus('error');
        setErrors([{
          row: 0,
          email: '',
          name: '',
          message: 'Failed to read file. Please try again.',
          failedField: 'File Reading'
        }]);
      }
    }, 0);
  };

  // Process data in chunks to keep UI responsive
  const processDataInChunks = (rawData) => {
    // Filter out empty rows and sample row
    const filteredData = rawData.filter(row => {
      const firstName = row["First Name"] || row["First Name*"];
      const isExampleRow = firstName && firstName.toString().includes("Example:");
      const isEmptyRow = !firstName || firstName.toString().trim() === "";
      return !isExampleRow && !isEmptyRow;
    });

    if (filteredData.length === 0) {
      setStatus('error');
      setErrors([{
        row: 0,
        email: '',
        name: '',
        message: "No valid student records found. Please add actual student data.",
        failedField: 'Validation'
      }]);
      return;
    }

    setProgress(prev => ({
      ...prev,
      step: 3,
      stepName: 'Validating',
      subStepName: `Preparing to validate ${filteredData.length} records...`,
      total: filteredData.length,
      current: 0,
      percentage: 30
    }));

    // Detect columns from first row
    const firstRow = filteredData[0];
    const hasSemester = Object.keys(firstRow).some(key => 
      key.toLowerCase().includes("semester")
    );
    const hasTerm = Object.keys(firstRow).some(key => 
      key.toLowerCase().includes("term")
    );

    // Process in chunks of 50
    const CHUNK_SIZE = 50;
    const validStudents = [];
    const validationErrors = [];
    let processedCount = 0;

    const processNextChunk = () => {
      const startIdx = processedCount;
      const endIdx = Math.min(startIdx + CHUNK_SIZE, filteredData.length);
      
      for (let i = startIdx; i < endIdx; i++) {
        const row = filteredData[i];
        const rowNum = i + 2;
        
        const result = validateRow(row, rowNum, hasSemester, hasTerm);
        
        if (result.error) {
          validationErrors.push(result.error);
        } else {
          validStudents.push(result.student);
        }
      }
      
      processedCount = endIdx;
      
      // Calculate percentage (30% to 70% range for validation)
      const validationPercentage = 30 + Math.floor((processedCount / filteredData.length) * 40);
      
      setProgress(prev => ({
        ...prev,
        current: processedCount,
        subStepName: `Validating ${processedCount}/${filteredData.length} records...`,
        percentage: validationPercentage
      }));
      
      if (processedCount < filteredData.length) {
        // Process next chunk after a small delay
        setTimeout(processNextChunk, 10);
      } else {
        // Validation complete
        finishValidation(validStudents, validationErrors, filteredData.length);
      }
    };

    // Start processing first chunk
    setTimeout(processNextChunk, 0);
  };

  // Validate a single row
  const validateRow = (row, rowNum, hasSemester, hasTerm) => {
    try {
      const errors = [];
      
      // Required fields
      if (!row["First Name"] && !row["First Name*"]) errors.push("First Name is required");
      if (!row["Last Name"] && !row["Last Name*"]) errors.push("Last Name is required");
      if (!row["Student Email"] && !row["Student Email*"]) errors.push("Email is required");
      if (!row["Parent Name"] && !row["Parent Name*"]) errors.push("Parent Name is required");
      
      // Academic fields
      if (!row["Program Type"] && !row["Program Type*"]) errors.push("Program Type is required");
      if (!row["Program Name"] && !row["Program Name*"]) errors.push("Program Name is required");
      if (!row["Academic Year"] && !row["Academic Year*"]) errors.push("Academic Year is required");
      
      // Semester or Term validation
      if (hasSemester && !row["Semester"] && !row["Semester*"]) {
        errors.push("Semester is required for this template");
      }
      if (hasTerm && !row["Term"] && !row["Term*"]) {
        errors.push("Term is required for this template");
      }

      if (errors.length > 0) {
        return {
          error: {
            row: rowNum,
            email: row["Student Email"] || row["Student Email*"] || 'Unknown',
            name: `${row["First Name"] || row["First Name*"] || ''} ${row["Last Name"] || row["Last Name*"] || ''}`.trim() || 'Unknown',
            message: errors.join(', '),
            failedField: 'Validation'
          }
        };
      }

      // Build student object
      const student = {
        rowNum,
        programTypeName: row["Program Type"] || row["Program Type*"],
        programName: row["Program Name"] || row["Program Name*"],
        academicYearName: row["Academic Year"] || row["Academic Year*"],
        termLabel: row["Term"] || row["Term*"],
        semesterName: row["Semester"] || row["Semester*"],
        preferredClassName: row["Preferred Class"] || "",
        
        firstName: row["First Name"] || row["First Name*"],
        lastName: row["Last Name"] || row["Last Name*"],
        dateOfBirth: parseExcelDate(row["Date of Birth"] || row["Date of Birth* (DD/MM/YYYY)"]),
        gender: row["Gender"] || row["Gender* (Male/Female/Other)"],
        studentCountryCode: row["Student Country Code"] || row["Student Country Code* (+91)"] || "+91",
        studentPhoneNumber: row["Student Phone"] || row["Student Phone* (10 digits)"] || "",
        email: row["Student Email"] || row["Student Email*"],
        bloodGroup: row["Blood Group"] || "",
        category: row["Category"] || "",
        nationality: row["Nationality"] || "Indian",
        admissionType: row["Admission Type"] || "Regular",
        
        parentName: row["Parent Name"] || row["Parent Name*"],
        parentCountryCode: row["Parent Country Code"] || row["Parent Country Code (+91)"] || "+91",
        parentPhoneNumber: row["Parent Phone"] || row["Parent Phone (10 digits)"] || "",
        parentEmail: row["Parent Email"] || "",
        
        address: row["Permanent Address"] || "",
        city: row["City"] || "",
        state: row["State"] || "",
        pincode: row["Pincode"] || "",
        country: row["Country"] || "India",
        
        USN: row["USN"] || "",
        remarks: row["Remarks"] || ""
      };

      // Clean phone numbers
      if (student.studentPhoneNumber) {
        student.studentPhoneNumber = student.studentPhoneNumber.replace(/\D/g, '');
      }
      if (student.parentPhoneNumber) {
        student.parentPhoneNumber = student.parentPhoneNumber.replace(/\D/g, '');
      }

      // Ensure country codes have +
      if (student.studentCountryCode && !student.studentCountryCode.startsWith('+')) {
        student.studentCountryCode = `+${student.studentCountryCode}`;
      }
      if (student.parentCountryCode && !student.parentCountryCode.startsWith('+')) {
        student.parentCountryCode = `+${student.parentCountryCode}`;
      }

      return { student };
      
    } catch (error) {
      return {
        error: {
          row: rowNum,
          email: row["Student Email"] || row["Student Email*"] || 'Unknown',
          name: `${row["First Name"] || row["First Name*"] || ''} ${row["Last Name"] || row["Last Name*"] || ''}`.trim() || 'Unknown',
          message: error.message,
          failedField: 'Validation'
        }
      };
    }
  };

  // Finish validation and start processing
  const finishValidation = (validStudents, validationErrors, totalRows) => {
    if (validStudents.length === 0) {
      setReport({
        total: validationErrors.length,
        success: 0,
        failed: validationErrors.length
      });
      setErrors(validationErrors);
      
      const summary = {};
      validationErrors.forEach(err => {
        summary[err.failedField] = (summary[err.failedField] || 0) + 1;
      });
      setErrorSummary(summary);
      setStatus('completed_with_errors');
      return;
    }

    // Update progress for processing phase
    setProgress(prev => ({
      ...prev,
      step: 4,
      stepName: 'Processing',
      subStepName: `Preparing to process ${validStudents.length} students...`,
      current: 0,
      total: validStudents.length + validationErrors.length,
      percentage: 70
    }));

    // Send to backend
    processBatches(validStudents, validationErrors);
  };

  // --- 4. BATCH PROCESSING WITH PROGRESS ---
  const processBatches = async (validStudents, initialErrors) => {
    const BATCH_SIZE = 50;
    const totalBatches = Math.ceil(validStudents.length / BATCH_SIZE);
    
    // allErrors is no longer used; errors are now tracked in backendErrors
    let successCount = 0;

    setStatus('processing');
    setProgress(prev => ({
      ...prev,
      step: 4,
      stepName: 'Processing',
      subStepName: `Processing batch 1/${totalBatches}...`,
      total: validStudents.length + initialErrors.length,
      current: initialErrors.length,
      percentage: 70
    }));

    let backendSummary = null;
    let backendErrors = [];
    for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
      if (abortControllerRef.current?.signal.aborted) {
        console.log('Processing aborted');
        return;
      }
      const start = batchIndex * BATCH_SIZE;
      const end = Math.min(start + BATCH_SIZE, validStudents.length);
      const batch = validStudents.slice(start, end);
      const batchProgress = Math.floor((batchIndex / totalBatches) * 30);
      const currentPercentage = 70 + batchProgress;
      setProgress(prev => ({
        ...prev,
        subStepName: `Processing batch ${batchIndex + 1}/${totalBatches} (${batch.length} students)...`,
        percentage: currentPercentage
      }));
      try {
        const response = await api.post('/api/students/bulk-add', {
          students: batch,
          tenantId
        }, {
          signal: abortControllerRef.current?.signal
        });
        // If backend provides summary and results, use them
        if (response.data && response.data.summary) {
          backendSummary = response.data.summary;
        }
        // Use failedResults and successResults if present
        if (response.data && response.data.failedResults) {
          response.data.failedResults.forEach(r => {
            backendErrors.push({
              row: r.row || 'unknown',
              email: r.email || 'unknown',
              name: r.student ? `${r.student.firstName} ${r.student.lastName}` : 'Unknown',
              message: r.message || 'Processing failed',
              failedField: r.failedField || 'Server Error'
            });
          });
        } else if (response.data && response.data.results) {
          response.data.results.forEach(r => {
            if (r.status === 'failed') {
              backendErrors.push({
                row: r.row || 'unknown',
                email: r.email || 'unknown',
                name: r.student ? `${r.student.firstName} ${r.student.lastName}` : 'Unknown',
                message: r.message || 'Processing failed',
                failedField: r.failedField || 'Server Error'
              });
            }
          });
        }
        if (response.data && response.data.successResults) {
          successCount += response.data.successResults.length;
        } else if (response.data && response.data.results) {
          const batchSuccesses = response.data.results.filter(r => r.status === 'success').length;
          successCount += batchSuccesses;
        }
        const processed = initialErrors.length + Math.min(end, validStudents.length);
        setProgress(prev => ({
          ...prev,
          current: processed,
          subStepName: `Processed ${processed} of ${prev.total} records`,
          percentage: 70 + Math.floor(((batchIndex + 1) / totalBatches) * 30)
        }));
      } catch (error) {
        if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
          console.log('Request canceled');
          return;
        }
        console.error(`Batch ${batchIndex + 1} failed:`, error);
        batch.forEach(student => {
          backendErrors.push({
            row: student.rowNum || 'unknown',
            email: student.email,
            name: `${student.firstName} ${student.lastName}`,
            message: error.response?.data?.message || error.message || 'Server error',
            failedField: 'Server Error'
          });
        });
      }
      if (batchIndex < totalBatches - 1) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }
    // Prefer backend summary if available
    let reportSummary = backendSummary ? {
      total: backendSummary.total,
      success: backendSummary.success,
      failed: backendSummary.failed,
      ...backendSummary.records
    } : {
      total: validStudents.length + initialErrors.length,
      success: successCount,
      failed: backendErrors.length
    };
    // Use backend errors if available
    const summary = {};
    backendErrors.forEach(err => {
      summary[err.failedField] = (summary[err.failedField] || 0) + 1;
    });
    setReport(reportSummary);
    setErrors(backendErrors);
    setErrorSummary(summary);
    setStatus(backendErrors.length > 0 ? 'completed_with_errors' : 'success');
    setProgress(prev => ({
      ...prev,
      step: 5,
      stepName: 'Complete',
      subStepName: 'Import completed',
      current: prev.total,
      percentage: 100
    }));
    if (reportSummary.success > 0) {
      onBulkAddComplete();
    }
  };

  // --- 5. RENDER HELPERS ---
  const getStepPercentage = () => {
    return progress.percentage || 0;
  };

  const renderProgressSteps = () => {
    const steps = [
      { id: 1, name: 'Upload', icon: '📤' },
      { id: 2, name: 'Parse', icon: '🔍' },
      { id: 3, name: 'Validate', icon: '✓' },
      { id: 4, name: 'Process', icon: '⚙️' },
      { id: 5, name: 'Complete', icon: '✨' }
    ];

    const currentStep = progress.step;

    return (
      <div className="flex items-center justify-between mb-6 relative">
        <div className="absolute top-4 left-0 right-0 h-0.5 bg-gray-200 -z-10"></div>
        {steps.map((step, index) => (
          <div key={step.id} className="flex flex-col items-center relative z-10">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium mb-1 transition-all ${
              step.id < currentStep ? 'bg-green-500 text-white' :
              step.id === currentStep ? 'bg-blue-600 text-white scale-110 shadow-lg animate-pulse' :
              'bg-gray-200 text-gray-500'
            }`}>
              {step.id < currentStep ? '✓' : step.icon}
            </div>
            <span className={`text-xs ${
              step.id === currentStep ? 'text-blue-600 font-medium' : 'text-gray-500'
            }`}>
              {step.name}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const renderProgressBar = () => {
    const percentage = getStepPercentage();

    return (
      <div className="mb-4">
        <div className="flex justify-between items-center mb-1">
          <div className="flex items-center">
            <span className="text-sm font-medium text-gray-700">{progress.stepName}</span>
            {fileInfo.name && status !== 'idle' && status !== 'generating' && (
              <span className="ml-2 text-xs text-gray-500">
                {fileInfo.name} ({fileInfo.records} records)
              </span>
            )}
          </div>
          <span className="text-sm font-semibold text-blue-600">{percentage}%</span>
        </div>
        <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full transition-all duration-300 ease-out"
            style={{ width: `${percentage}%` }}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span className="flex items-center">
            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            {progress.current} of {progress.total} records
          </span>
          <span className="flex items-center">
            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            {progress.elapsedTime}
          </span>
        </div>
        {progress.subStepName && (
          <div className="mt-2 p-2 bg-blue-50 border border-blue-100 rounded-lg">
            <p className="text-xs text-blue-700 flex items-center">
              <svg className="w-3 h-3 mr-1 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {progress.subStepName}
            </p>
          </div>
        )}
      </div>
    );
  };

  const renderErrorTable = () => {
    if (errors.length === 0) return null;

    return (
      <div className="mt-6 border border-red-200 rounded-lg overflow-hidden">
        <div className="bg-red-50 px-4 py-2 border-b border-red-200 flex justify-between items-center">
          <span className="font-medium text-red-800">
            Failed Records ({errors.length})
          </span>
          <button
            onClick={() => {
              const csvContent = [
                ['Row', 'Name', 'Email', 'Error Type', 'Message'],
                ...errors.map(e => [e.row, e.name, e.email, e.failedField, e.message])
              ].map(row => row.join(',')).join('\n');
              
              const blob = new Blob([csvContent], { type: 'text/csv' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `failed_records_${new Date().toISOString().slice(0,10)}.csv`;
              a.click();
            }}
            className="text-sm text-red-600 hover:text-red-800 font-medium flex items-center"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Export CSV
          </button>
        </div>
        <div className="max-h-60 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-4 py-2 text-left">Row</th>
                <th className="px-4 py-2 text-left">Student</th>
                <th className="px-4 py-2 text-left">Error</th>
              </tr>
            </thead>
            <tbody>
              {errors.slice(0, 50).map((err, idx) => (
                <tr key={idx} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-2 font-mono text-gray-500">{err.row}</td>
                  <td className="px-4 py-2">
                    <div className="font-medium">{err.name || 'Unknown'}</div>
                    <div className="text-xs text-gray-500">{err.email || 'No email'}</div>
                  </td>
                  <td className="px-4 py-2">
                    <span className={`inline-block px-2 py-1 text-xs font-medium rounded ${
                      err.failedField === 'Validation' ? 'bg-yellow-100 text-yellow-800' :
                      err.failedField === 'Server Error' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {err.failedField}
                    </span>
                    <div className="text-red-600 text-xs mt-1">{err.message}</div>
                  </td>
                </tr>
              ))}
              {errors.length > 50 && (
                <tr>
                  <td colSpan="3" className="px-4 py-2 text-center text-gray-500 text-sm bg-gray-50">
                    ... and {errors.length - 50} more errors. Export CSV to see all.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  if (!showBulkAddModal) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Bulk Student Import</h2>
              <p className="text-sm text-gray-600 mt-1">
                Import up to 10,000+ students using Excel template
              </p>
            </div>
            <button
              onClick={onCloseBulkAdd}
              className="text-gray-500 hover:text-gray-700 text-xl p-2 hover:bg-white rounded-lg transition"
              disabled={status === 'processing'}
            >
              ×
            </button>
          </div>
        </div>

        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileUpload}
          className="hidden"
          accept=".xlsx,.xls"
        />

        <div className="flex-1 overflow-y-auto p-6">
          {/* Step 1: Download Template */}
          {status === 'idle' && (
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
                <h3 className="font-semibold text-blue-800 mb-4 flex items-center">
                  <span className="bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm mr-2">1</span>
                  Download Template
                </h3>
                
                <div className="mb-4">
                  <button
                    onClick={handleDownloadTemplate}
                    disabled={status === 'generating'}
                    className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-medium hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center shadow-md hover:shadow-lg"
                  >
                    {status === 'generating' ? (
                      <>
                        <svg className="animate-spin h-4 w-4 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Generating...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        Download Excel Template
                      </>
                    )}
                  </button>
                </div>

                <div className="text-sm text-gray-600">
                  <p className="mb-2">📋 Template includes:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li><span className="font-medium">Student Data</span> - Fill student information</li>
                    <li><span className="font-medium">Reference</span> - ALL valid academic combinations (all standards 1-10)</li>
                    <li><span className="font-medium">Instructions</span> - Complete guide</li>
                  </ul>
                </div>

                {academicType.hasSemester && academicType.hasTerms && (
                  <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800 flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.998-.833-2.732 0L4.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                      <span className="font-medium">Mixed academic system:</span> Both Semester and Term columns are present.
                    </p>
                  </div>
                )}
              </div>

              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors">
                <div className="text-5xl mb-4 text-gray-400">📤</div>
                <p className="font-semibold text-gray-800 text-xl mb-2">Step 2: Upload Completed File</p>
                <p className="text-gray-600 mb-4">
                  Upload your filled Excel file (supports up to 10,000+ records)
                </p>
                
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg font-medium hover:from-green-700 hover:to-emerald-700 transition-all duration-300 flex items-center mx-auto shadow-md hover:shadow-lg"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  Choose Excel File
                </button>
                
                <p className="text-xs text-gray-400 mt-4">
                  .xlsx or .xls files only • No size limit • Large files processed in chunks
                </p>
              </div>
            </div>
          )}

          {/* Progress View */}
          {['uploading', 'parsing', 'validating', 'processing'].includes(status) && (
            <div className="space-y-4">
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                {renderProgressSteps()}
                {renderProgressBar()}
              </div>
            </div>
          )}

          {/* Results View */}
          {['success', 'completed_with_errors', 'error'].includes(status) && report && (
            <div className="space-y-4">
              <div className={`p-6 rounded-lg ${
                status === 'success' ? 'bg-green-50 border border-green-200' :
                status === 'completed_with_errors' ? 'bg-yellow-50 border border-yellow-200' :
                'bg-red-50 border border-red-200'
              }`}>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-bold text-xl">
                      {status === 'success' ? '✅ Import Complete!' :
                       status === 'completed_with_errors' ? '⚠️ Completed with Errors' :
                       '❌ Import Failed'}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Processed {report.total} records in {progress.elapsedTime}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-500">Default Password</div>
                    <div className="font-mono font-bold text-lg text-blue-600 bg-blue-50 px-3 py-1 rounded-lg border border-blue-200">
                      Truyon@1234
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="bg-white p-4 rounded-lg border border-gray-200 text-center">
                    <div className="text-3xl font-bold text-gray-800">{report.total}</div>
                    <div className="text-sm text-gray-500">Total Records</div>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-green-200 text-center">
                    <div className="text-3xl font-bold text-green-600">{report.success}</div>
                    <div className="text-sm text-gray-500">Successfully Added</div>
                  </div>
                  <div className={`bg-white p-4 rounded-lg border text-center ${
                    report.failed > 0 ? 'border-red-200' : 'border-gray-200'
                  }`}>
                    <div className={`text-3xl font-bold ${report.failed > 0 ? 'text-red-600' : 'text-gray-800'}`}>
                      {report.failed}
                    </div>
                    <div className="text-sm text-gray-500">Failed</div>
                  </div>
                </div>

                {Object.keys(errorSummary).length > 0 && (
                  <div className="mt-4">
                    <h4 className="font-medium text-gray-700 mb-2">Error Summary:</h4>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(errorSummary).map(([type, count]) => (
                        <span key={type} className="px-3 py-1.5 bg-red-100 text-red-800 rounded-lg text-sm font-medium border border-red-200">
                          {type}: <span className="font-bold ml-1">{count}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {renderErrorTable()}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-between items-center">
            {status === 'processing' && (
              <button
                onClick={() => {
                  if (abortControllerRef.current) {
                    abortControllerRef.current.abort();
                    setStatus('idle');
                  }
                }}
                className="px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors"
              >
                Cancel Import
              </button>
            )}
            <div className="flex-1"></div>
            <div className="flex space-x-3">
              {report ? (
                <>
                  <button
                    onClick={resetState}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Import More
                  </button>
                  <button
                    onClick={onCloseBulkAdd}
                    className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors"
                  >
                    Close
                  </button>
                </>
              ) : (
                <button
                  onClick={onCloseBulkAdd}
                  className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors"
                  disabled={status === 'processing'}
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkOperations;